import React from 'react';
import {
  FlexiblePageTreeNode,
  FlexiblePageTreeNodeType,
  SectionTreeNode,
  ComponentTreeNode,
  ButtonTreeNode,
  StructuredButtonTreeNode,
} from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/flexiblePagesTreeNode';
import { RenderStage } from '@mutiny-pkg/champaign-schemas/src/schemas/micrositePages/render';
import { Button } from '@mutiny-pkg/bin-ui/src/flexiblePages/components/Button';
import { StructuredButton } from '@mutiny-pkg/bin-ui/src/flexiblePages/components/StructuredButton';
import { Container } from '@mutiny-pkg/bin-ui/src/flexiblePages/components/Container';
import { Section } from '@mutiny-pkg/bin-ui/src/flexiblePages/components/Section';
import { Icon } from '@mutiny-pkg/bin-ui/src/flexiblePages/components/Icon';
import { Image } from '@mutiny-pkg/bin-ui/src/flexiblePages/components/Image';
import { Svg } from '@mutiny-pkg/bin-ui/src/flexiblePages/components/Svg';
import { Text } from '@mutiny-pkg/bin-ui/src/flexiblePages/components/Text';
import { Video } from '@mutiny-pkg/bin-ui/src/flexiblePages/components/Video';
import { Embed } from '@mutiny-pkg/bin-ui/src/flexiblePages/components/Embed';
import { Dialog } from '@mutiny-pkg/bin-ui/src/flexiblePages/components/Dialog';
import { TabGroup } from '@mutiny-pkg/bin-ui/src/flexiblePages/components/TabGroup';
import { TabList } from '@mutiny-pkg/bin-ui/src/flexiblePages/components/TabList';
import { TabPanels } from '@mutiny-pkg/bin-ui/src/flexiblePages/components/TabPanels';
import { Disclosure, DisclosureContent } from '@mutiny-pkg/bin-ui/src/flexiblePages/components/Disclosure';
import { Page } from '@mutiny-pkg/bin-ui/src/flexiblePages/components/Page';
import { stripLinksFromTreeNode } from '@mutiny-pkg/bin-ui/src/flexiblePages/utils/stripLinksFromTreeNode';

interface ReadonlyComponentRendererProps {
  componentTree: FlexiblePageTreeNode;
  overrideVariables?: Record<string, string>;
  staticAssetsBaseURL?: string | null;
  renderStage?: RenderStage;
  children?: React.ReactNode;
}

export const ReadonlyComponentRenderer = React.memo(
  ({
    componentTree,
    overrideVariables,
    staticAssetsBaseURL,
    renderStage,
    children,
  }: ReadonlyComponentRendererProps) => {
    const emptyMentionItems = React.useMemo(() => [], []);
    const emptyRefetch = React.useCallback(async () => {}, []);

    // Only pass staticAssetsBaseURL in Preview renderStage, not in Publish
    const resolvedstaticAssetsBaseURL = renderStage === RenderStage.Preview ? staticAssetsBaseURL : undefined;

    const renderComponentNode = (treeNode: ComponentTreeNode): React.ReactNode => {
      switch (treeNode.type) {
        case FlexiblePageTreeNodeType.Container: {
          return (
            <Container
              key={treeNode.id}
              id={treeNode.id}
              {...treeNode.data}
              staticAssetsBaseURL={resolvedstaticAssetsBaseURL}
            >
              {'children' in treeNode &&
                treeNode.children?.map((childNode: ComponentTreeNode) => renderComponentNode(childNode))}
            </Container>
          );
        }

        case FlexiblePageTreeNodeType.Text: {
          return (
            <Text
              key={treeNode.id}
              id={treeNode.id}
              {...treeNode.data}
              overrideVariables={overrideVariables}
              mentionItems={emptyMentionItems}
              refetchMentionItems={emptyRefetch}
              isEditor={false}
            />
          );
        }

        case FlexiblePageTreeNodeType.Button: {
          return (
            <Button
              key={treeNode.id}
              id={treeNode.id}
              {...treeNode.data}
              overrideVariables={overrideVariables}
              mentionItems={emptyMentionItems}
              refetchMentionitems={emptyRefetch}
              isEditor={false}
            />
          );
        }

        case FlexiblePageTreeNodeType.StructuredButton: {
          return (
            <StructuredButton key={treeNode.id} id={treeNode.id} {...treeNode.data} isEditor={false}>
              {'children' in treeNode &&
                treeNode.children?.map((childNode: ComponentTreeNode) =>
                  renderComponentNode(stripLinksFromTreeNode(childNode)),
                )}
            </StructuredButton>
          );
        }

        case FlexiblePageTreeNodeType.Image: {
          return (
            <Image
              key={treeNode.id}
              id={treeNode.id}
              {...treeNode.data}
              overrideVariables={overrideVariables}
              staticAssetsBaseURL={resolvedstaticAssetsBaseURL}
            />
          );
        }

        case FlexiblePageTreeNodeType.Video: {
          return <Video key={treeNode.id} id={treeNode.id} {...treeNode.data} />;
        }

        case FlexiblePageTreeNodeType.Svg: {
          return <Svg key={treeNode.id} id={treeNode.id} {...treeNode.data} />;
        }

        case FlexiblePageTreeNodeType.Icon: {
          return <Icon key={treeNode.id} id={treeNode.id} {...treeNode.data} />;
        }

        case FlexiblePageTreeNodeType.Embed: {
          return (
            <Embed
              key={treeNode.id}
              id={treeNode.id}
              {...treeNode.data}
              overrideVariables={overrideVariables}
              isEditor={false}
            />
          );
        }

        case FlexiblePageTreeNodeType.Dialog: {
          return (
            <Dialog key={treeNode.id} id={treeNode.id} {...treeNode.data}>
              {'children' in treeNode &&
                treeNode.children?.map((childNode: ComponentTreeNode) => renderComponentNode(childNode))}
            </Dialog>
          );
        }

        case FlexiblePageTreeNodeType.TabGroup: {
          return (
            <TabGroup
              {...treeNode.data}
              key={treeNode.id}
              id={treeNode.id}
              renderComponentNode={renderComponentNode}
              tabGroupNodes={treeNode.children}
            />
          );
        }

        case FlexiblePageTreeNodeType.TabList: {
          return (
            <TabList key={treeNode.id} id={treeNode.id} {...treeNode.data} data-no-dnd>
              {treeNode.children.map((tab) => {
                const tabNode = {
                  ...tab,
                  data: {
                    ...tab.data,
                    'data-no-dnd': true,
                  },
                };
                return renderComponentNode(tabNode as ButtonTreeNode | StructuredButtonTreeNode);
              })}
            </TabList>
          );
        }

        case FlexiblePageTreeNodeType.TabPanels: {
          return (
            <TabPanels key={treeNode.id} id={treeNode.id} {...treeNode.data} data-no-dnd>
              {treeNode.children.map((panel) => {
                const panelNode = {
                  ...panel,
                  data: {
                    ...panel.data,
                    'data-no-dnd': true,
                  },
                };
                return renderComponentNode(panelNode);
              })}
            </TabPanels>
          );
        }

        case FlexiblePageTreeNodeType.Disclosure: {
          const [buttonNode, disclosureContentNode] = treeNode.children;

          if (!buttonNode || !disclosureContentNode) {
            console.error('Invalid disclosure node structure');
            return null;
          }

          // Enhance button with command properties
          const enhancedButton = {
            ...buttonNode,
            data: {
              ...buttonNode.data,
              commandfor: disclosureContentNode.data.domTargetId,
              command: '--toggle' as const,
              className: buttonNode.data.className ? `${buttonNode.data.className} group` : 'group',
            },
          };

          return (
            <Disclosure {...treeNode.data} key={treeNode.id} id={treeNode.id}>
              {renderComponentNode(enhancedButton)}
              {renderComponentNode(disclosureContentNode)}
            </Disclosure>
          );
        }

        case FlexiblePageTreeNodeType.DisclosureContent: {
          return (
            <DisclosureContent {...treeNode.data} key={treeNode.id} id={treeNode.id}>
              {'children' in treeNode && treeNode.children?.map((childNode) => renderComponentNode(childNode))}
            </DisclosureContent>
          );
        }

        default: {
          console.warn(`Unknown component type: ${treeNode.type}`);
          return null;
        }
      }
    };

    const renderSectionNode = (sectionNode: SectionTreeNode): React.ReactNode => {
      return (
        <Section
          id={sectionNode.id}
          key={sectionNode.id}
          {...sectionNode.data}
          staticAssetsBaseURL={resolvedstaticAssetsBaseURL}
        >
          {sectionNode.children?.map((childNode: ComponentTreeNode) => renderComponentNode(childNode))}
        </Section>
      );
    };

    const renderPageNode = (pageTreeNode: FlexiblePageTreeNode): React.ReactNode => {
      return (
        <Page treeNode={pageTreeNode}>
          {children}
          {pageTreeNode.children?.map((childNode) => renderSectionNode(childNode))}
        </Page>
      );
    };

    return <>{renderPageNode(componentTree)}</>;
  },
  (prevProps, nextProps) => {
    return (
      prevProps.componentTree === nextProps.componentTree &&
      prevProps.overrideVariables === nextProps.overrideVariables &&
      prevProps.staticAssetsBaseURL === nextProps.staticAssetsBaseURL &&
      prevProps.renderStage === nextProps.renderStage &&
      prevProps.children === nextProps.children
    );
  },
);

ReadonlyComponentRenderer.displayName = 'ReadonlyComponentRenderer';
